<template>
	<transition name="modal">
		<div class="modal-overlay" @mousedown="close">
			<div
				class="modal-content"
				:class="{ fullscreen, 'hundred-height': safari }"
				ref="content"
				@mousedown.stop
			>
				<slot />
			</div>
		</div>
	</transition>
</template>

<script>
import { CloseIcon } from '../../icons'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { browser } from '@/store'

const selector =
	'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]'

export default {
	name: 'Modal',
	components: { CloseIcon },
	props: {
		onBeforeClose: {
			type: Function,
		},
		fullscreen: {
			type: Boolean,
			default: null,
		},
	},
	data() {
		return { tabData: [] }
	},

	emits: ['close'],

	computed: {
		safari() {
			return browser.name === 'safari'
		},
	},

	methods: {
		async close() {
			if (this.onBeforeClose) {
				const canClose = await this.onBeforeClose()
				if (!canClose) return
			}
			this.enableTabOutside()
			this.$emit('close')
		},

		preventTabOutside() {
			const tabbableElements = document.querySelectorAll(selector)
			this.tabData = Array.from(tabbableElements)
				// filter out any elements within the modal
				.filter((elem) => !this.$refs.content?.contains(elem))
				// store refs to the element and its original tabindex
				.map((elem) => {
					// capture original tab index, if it exists
					const tabIndex = elem.hasAttribute('tabindex')
						? elem.getAttribute('tabindex')
						: null
					// temporarily set the tabindex to -1
					elem.setAttribute('tabindex', -1)
					return { elem, tabIndex }
				})
		},
		enableTabOutside() {
			this.tabData.forEach(({ elem, tabIndex }) => {
				if (tabIndex === null) {
					elem.removeAttribute('tabindex')
				} else {
					elem.setAttribute('tabindex', tabIndex)
				}
			})
			this.tabData = []
		},
	},
	mounted() {
		this.$onEventBus('close-modal', this.close)
		this.escapeId = addEscapeListener(() => this.close())
		this.preventTabOutside()
	},
	beforeUnmount() {
		this.enableTabOutside()
		removeEscapeListener(this.escapeId)
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.modal-overlay {
	background: rgba(0, 0, 0, 0.39) !important;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	text-shadow: none;
	@include max-z;
	display: flex;
	justify-content: center;
	align-items: center;
}

.modal-content {
	position: relative;
	background: var(--neutral-0);
	border-radius: 12px;
	overflow: hidden;
	max-width: 80vw;
	max-height: 80vh;
	box-shadow: 0 8px 24px 0 #595959;
	&.fullscreen {
		height: 100vh;
		width: 100vw;
		max-height: 100vh;
		max-width: 100vw;
		border-radius: 0;
	}
}

.modal-enter {
	opacity: 0;
}

.modal-leave-active {
	opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
	transition: opacity 0.3s ease-in-out;
}

.hundred-height {
	max-height: 100vh !important;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.modal-overlay {
		.modal-content {
			background: var(--neutral-200);
			box-shadow: 0 8px 24px 0 #000;
		}
	}
}
</style>
